import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/track";

const COPY = {
  legacy: { title: "Want a copy of your results?", text: "We'll email your score and personalized summary — free.", consent: "I agree to receive my Legacy Checkup results by email from Legacy Wave Consulting.", button: "EMAIL MY RESULTS", done: "Done — your results are on the way to your inbox." },
  complete: { title: "Want a copy of your results?", text: "We'll email your score and personalized summary — free.", consent: "I agree to receive my Legacy Checkup results by email from Legacy Wave Consulting.", button: "EMAIL MY RESULTS", done: "Done — your results are on the way to your inbox." },
  family: { title: "Want a copy of your summary?", text: "We'll email a plain-English recap of what you shared and the areas that may be worth reviewing.", consent: "I agree to receive my Family Protection Checkup summary by email from Legacy Wave Consulting.", button: "EMAIL MY SUMMARY", done: "Done — your summary is on the way to your inbox." },
  medicare: { title: "Want a copy of your Medicare Checkup summary?", text: "We'll email a recap of the topics you asked about and what you shared.", consent: "I agree to receive my Medicare Checkup summary by email from Legacy Wave Consulting. Requesting this summary is not a request to enroll in any plan and does not give permission for Medicare plan marketing.", button: "EMAIL MY SUMMARY", done: "Done — your summary is on the way to your inbox." },
};

export default function EmailResultsCard({ assessmentId, pathwayKey = "complete" }) {
  const copy = COPY[pathwayKey] || COPY.complete;
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", consent_email: false, company: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post(`/assessments/${assessmentId}/contact`, form);
      track("report_requested", pathwayKey);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail ? "Please check your details and try again." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full min-h-[52px] rounded-xl border border-sand bg-white px-4 text-base text-navy placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition";

  return (
    <div className="mt-10 bg-white rounded-3xl border border-sand p-6 sm:p-8" data-testid="email-results-card">
      {done ? (
        <div className="flex items-center gap-3" data-testid="email-results-success">
          <CheckCircle2 className="w-6 h-6 text-[#15803D] shrink-0" />
          <p className="font-semibold text-navy">{copy.done}</p>
        </div>
      ) : (
        <form onSubmit={submit}>
          <h2 className="font-serif text-2xl font-semibold text-navy">{copy.title}</h2>
          <p className="mt-1 text-sm text-slate-600">{copy.text}</p>
          <input type="text" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ display: "none" }} />
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-navy mb-1.5" htmlFor="er-first">First Name</label>
              <input id="er-first" required data-testid="email-results-first-name" className={inputCls} value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} autoComplete="given-name" />
            </div>
            <div>
              <label className="block text-sm font-bold text-navy mb-1.5" htmlFor="er-last">Last Name</label>
              <input id="er-last" required data-testid="email-results-last-name" className={inputCls} value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} autoComplete="family-name" />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-bold text-navy mb-1.5" htmlFor="er-email">Email Address</label>
            <input id="er-email" type="email" required data-testid="email-results-email" className={inputCls} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} autoComplete="email" />
          </div>
          <label className="mt-4 flex items-start gap-3 cursor-pointer">
            <input type="checkbox" required checked={form.consent_email} onChange={(e) => setForm((f) => ({ ...f, consent_email: e.target.checked }))} data-testid="email-results-consent" className="mt-1 w-6 h-6 shrink-0 accent-[#C59B27]" />
            <span className="text-sm text-slate-700 leading-relaxed">{copy.consent}</span>
          </label>
          {error && <p className="mt-3 text-sm font-semibold text-[#B45309] bg-[#FDF3E3] border border-sand rounded-xl px-4 py-3" data-testid="email-results-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="email-results-button" className="mt-5 w-full min-h-[56px] rounded-full border-2 border-navy text-navy font-extrabold text-sm tracking-widest hover:bg-navy hover:text-cream transition-colors disabled:opacity-60">
            {loading ? "SENDING…" : copy.button}
          </button>
        </form>
      )}
    </div>
  );
}
